import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Flag, Coins, Star, Users, ShieldCheck, Zap, Ban, CalendarClock, Trophy, ChevronDown, Lightbulb,
} from "lucide-react";
import { api } from "@/lib/api";

function AccordionItem({ id, icon: Icon, title, children, open, toggle, testid }) {
  const isOpen = open === id;
  return (
    <div
      data-testid={testid}
      className="rounded-xl border border-[#3F3F4C] bg-[#2A2A32] overflow-hidden"
    >
      <button
        data-testid={`${testid}-toggle`}
        onClick={() => toggle(isOpen ? null : id)}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 min-h-[56px] text-left"
      >
        <span className="font-racing font-bold text-sm sm:text-base uppercase tracking-wide text-white flex items-center gap-2">
          <Icon className="w-5 h-5 text-[#FFB703] shrink-0" /> {title}
        </span>
        <ChevronDown
          className={`w-5 h-5 text-gray-400 shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180 text-[#FF1E27]" : ""}`}
        />
      </button>
      {isOpen && <div className="px-5 pb-5 pt-1 border-t border-[#3F3F4C]/60">{children}</div>}
    </div>
  );
}

function PointRow({ label, pts, cls = "text-white" }) {
  return (
    <div className="flex justify-between px-4 py-1.5 text-sm border-b border-[#242429] last:border-0">
      <span className="font-body-sport text-gray-300">{label}</span>
      <span className={`font-time font-bold ${cls}`}>{pts}</span>
    </div>
  );
}

export default function Regolamento() {
  const [open, setOpen] = useState("deadline");
  const { data: settings } = useQuery({
    queryKey: ["settings-public"],
    queryFn: async () => (await api.get("/settings/public")).data,
  });
  const custom = settings?.content?.regolamento;
  const race = settings?.race || {};
  const toggle = (id) => setOpen(id);

  const fmtDate = (d) =>
    d ? new Date(d).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

  return (
    <div data-testid="regolamento-page" className="space-y-6 max-w-4xl">
      <h2 className="font-racing font-bold text-xl sm:text-2xl uppercase tracking-wide text-white border-l-4 border-[#FF1E27] pl-3">
        Regolamento & Guida al Gioco
      </h2>

      {custom && (
        <div className="rounded-xl border border-[#FFB703]/50 bg-[#FFB703]/5 p-6">
          <h3 className="font-racing font-bold text-sm uppercase tracking-widest text-[#FFB703] mb-3">
            Note Ufficiali dell'Organizzazione
          </h3>
          <p className="text-sm text-gray-200 whitespace-pre-line leading-relaxed" data-testid="regolamento-custom">
            {custom}
          </p>
        </div>
      )}

      <div className="rounded-xl border border-[#FFB703] bg-[#FFB703]/10 p-5 flex items-start gap-3" data-testid="strategy-tip">
        <Lightbulb className="w-6 h-6 text-[#FFB703] shrink-0 mt-0.5" />
        <p className="text-sm text-[#FFB703] font-body-sport font-semibold leading-relaxed">
          💡 Consiglio Strategico: Guarda le Prove del Sabato prima di confermare il tuo Capitano per la Domenica!
        </p>
      </div>

      <div className="space-y-3">
        <AccordionItem id="deadline" icon={CalendarClock} title="Date & Deadline" open={open} toggle={toggle} testid="rules-deadline">
          <ul className="space-y-2.5 text-sm text-gray-300">
            <li>• Le formazioni devono essere <strong className="text-white">salvate entro le ore 07:00 del 06/11, giorno delle Prove</strong> ({fmtDate(race.lock_at)}).</li>
            <li>• Oltre questa data e ora la squadra sarà <strong className="text-white">congelata definitivamente</strong>: i 18 piloti non potranno più essere sostituiti.</li>
            <li>• Unica eccezione: la finestra di cambio Capitano tra fine Prove e inizio Gara ({fmtDate(race.captain_window_start)} → {fmtDate(race.captain_window_end)}).</li>
          </ul>
        </AccordionItem>

        <AccordionItem id="squadra" icon={Flag} title="Come Creare la Squadra" open={open} toggle={toggle} testid="rules-squadra">
          <ul className="space-y-3 text-sm text-gray-300">
            <li className="flex items-start gap-3">
              <Coins className="w-4 h-4 text-[#FFB703] mt-0.5 shrink-0" />
              <span>Hai <strong className="text-white">120 Fantacrediti</strong> per acquistare i piloti iscritti alla 69° Salita dei Monti Iblei.</span>
            </li>
            <li className="flex items-start gap-3">
              <Users className="w-4 h-4 text-[#FFB703] mt-0.5 shrink-0" />
              <span>La scuderia è composta da esattamente <strong className="text-white">18 piloti</strong> — massimo 1 per ogni Gruppo; il costo totale non può superare il budget.</span>
            </li>
            <li className="flex items-start gap-3">
              <Star className="w-4 h-4 text-[#FFB703] mt-0.5 shrink-0" />
              <span>Nomina un <strong className="text-white">Capitano</strong>: i suoi punti valgono <strong className="text-[#FFB703]">x1.5</strong>.</span>
            </li>
          </ul>
        </AccordionItem>

        <AccordionItem id="weekend" icon={Zap} title="Struttura del Weekend (2 Giorni, 4 Manche)" open={open} toggle={toggle} testid="rules-weekend">
          <ul className="space-y-2.5 text-sm text-gray-300">
            <li>• <strong className="text-white">Giorno 1 — Prove (Sabato):</strong> Manche 1 Prove + Manche 2 Prove.</li>
            <li>• <strong className="text-white">Giorno 2 — Gara (Domenica):</strong> Manche 1 Gara + Manche 2 Gara.</li>
            <li>• Il punteggio totale della squadra deriva <strong className="text-white">sia dalle Prove che dalla Gara</strong>: ogni manche conta.</li>
            <li>• Le posizioni assolute e di classe che assegnano i punti principali sono determinate dalla <strong className="text-white">somma dei tempi delle 2 manche di Gara</strong>.</li>
            <li>• Segui la scheda "Classifica Parziale & Live" con filtri Solo Prove / Solo Gara / Totale Weekend.</li>
          </ul>
        </AccordionItem>

        <AccordionItem id="capitano" icon={Star} title="Regola del Capitano" open={open} toggle={toggle} testid="rules-capitano">
          <ul className="space-y-2.5 text-sm text-gray-300">
            <li>• Il Capitano scelto alla creazione vale x1.5 su tutti i suoi punti del weekend.</li>
            <li>• <strong className="text-white">Cambio strategico:</strong> al termine delle Prove del Sabato si apre una finestra temporale in cui puoi <strong className="text-[#FFB703]">cambiare il Capitano</strong> tra i 18 piloti già in squadra, prima dell'inizio della Gara di Domenica.</li>
            <li>• Fuori da questa finestra la formazione è bloccata in ogni sua parte.</li>
          </ul>
        </AccordionItem>

        <AccordionItem id="punti-gara" icon={Trophy} title="Punti Gara (Domenica)" open={open} toggle={toggle} testid="rules-punti-gara">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="rounded-lg border border-[#3F3F4C] bg-[#121214] overflow-hidden">
              <p className="px-4 py-2 font-body-sport text-[10px] font-bold tracking-widest text-gray-500 uppercase border-b border-[#3F3F4C]">
                Posizione Assoluta (somma manche gara)
              </p>
              {[["1°", "50 pt"], ["2°", "45 pt"], ["3°", "40 pt"], ["4°", "35 pt"], ["5°", "30 pt"], ["6°", "25 pt"], ["7°", "20 pt"], ["8°", "15 pt"], ["9°", "10 pt"], ["10°", "5 pt"], ["Oltre il 10°", "2 pt"]].map(([l, pt]) => (
                <PointRow key={l} label={`${l} Assoluto`} pts={pt} />
              ))}
            </div>
            <div className="space-y-4">
              <div className="rounded-lg border border-[#3F3F4C] bg-[#121214] overflow-hidden">
                <p className="px-4 py-2 font-body-sport text-[10px] font-bold tracking-widest text-gray-500 uppercase border-b border-[#3F3F4C]">
                  Posizione di Classe / Gruppo
                </p>
                <PointRow label="1° di Classe" pts="+20 pt" cls="text-[#22C55E]" />
                <PointRow label="2° di Classe" pts="+15 pt" cls="text-[#22C55E]" />
                <PointRow label="3° di Classe" pts="+10 pt" cls="text-[#22C55E]" />
              </div>
              <div className="rounded-lg border border-[#3F3F4C] bg-[#121214] overflow-hidden">
                <p className="px-4 py-2 font-body-sport text-[10px] font-bold tracking-widest text-gray-500 uppercase border-b border-[#3F3F4C]">
                  Bonus & Malus Gara
                </p>
                <PointRow label="Miglioramento Gara M2 su M1" pts="+5 pt" cls="text-[#22C55E]" />
                <PointRow label="Ritiro / Squalifica in Gara" pts="-10 pt" cls="text-[#EF4444]" />
              </div>
            </div>
          </div>
        </AccordionItem>

        <AccordionItem id="punti-prove" icon={Zap} title="Punti Prove (Sabato)" open={open} toggle={toggle} testid="rules-punti-prove">
          <div className="rounded-lg border border-[#3F3F4C] bg-[#121214] overflow-hidden">
            <p className="px-4 py-2 font-body-sport text-[10px] font-bold tracking-widest text-gray-500 uppercase border-b border-[#3F3F4C]">
              Bonus & Malus specifici del Giorno di Prova
            </p>
            <PointRow label='BONUS "Miglioramento Costante" (migliora tra Prove M1 e M2)' pts="+3 pt" cls="text-[#22C55E]" />
            <PointRow label='BONUS "Top Setter Prove" (miglior tempo assoluto di classe nelle Prove)' pts="+5 pt" cls="text-[#22C55E]" />
            <PointRow label='MALUS "Saltata Manche Prove" (non prende il via per problemi tecnici)' pts="-4 pt" cls="text-[#EF4444]" />
            <PointRow label='MALUS "Assetto Sbagliato / DNF Prove" (testacoda o fuori strada)' pts="-5 pt" cls="text-[#EF4444]" />
          </div>
          <p className="text-xs text-gray-500 mt-3">
            I punti delle Prove si sommano a quelli della Gara nel totale del pilota (e quindi della tua squadra).
          </p>
        </AccordionItem>

        <AccordionItem id="eventi" icon={Zap} title="Eventi Speciali (Bonus/Malus Extra)" open={open} toggle={toggle} testid="rules-eventi">
          <p className="text-sm text-gray-300">
            La Direzione può assegnare Bonus e Malus personalizzati (es. "Intervista Simpatica nei Paddock", "Testa a Coda",
            "Livrea Speciale") visibili sulle card dei piloti con il dettaglio trasparente nel calcolo della tua squadra.
          </p>
        </AccordionItem>

        <AccordionItem id="leghe" icon={Users} title="Leghe Pubbliche e Private" open={open} toggle={toggle} testid="rules-leghe">
          <ul className="space-y-3 text-sm text-gray-300">
            <li className="flex items-start gap-3">
              <Trophy className="w-4 h-4 text-[#FFB703] mt-0.5 shrink-0" />
              <span>Ogni iscritto partecipa automaticamente alla <strong className="text-white">Classifica Generale</strong>.</span>
            </li>
            <li className="flex items-start gap-3">
              <Users className="w-4 h-4 text-[#FFB703] mt-0.5 shrink-0" />
              <span>Le <strong className="text-white">Leghe Private</strong> hanno codice invito, link diretto, limite partecipanti (default 20) e classifica visibile ai soli membri.</span>
            </li>
            <li className="flex items-start gap-3">
              <ShieldCheck className="w-4 h-4 text-[#FFB703] mt-0.5 shrink-0" />
              <span>Le leghe private sono <strong className="text-white">invisibili agli esterni</strong>.</span>
            </li>
          </ul>
        </AccordionItem>

        <AccordionItem id="decreto" icon={Flag} title="Decreto Vincitore" open={open} toggle={toggle} testid="rules-decreto">
          <p className="text-sm text-gray-300">
            Le classifiche pubblicate durante il weekend sono da considerarsi <strong className="text-white">ufficiose</strong>.
            La classifica finale diventa <strong className="text-[#FFB703]">UFFICIALE solo dopo il decreto di "Fine Competizione"</strong>{" "}
            da parte della Direzione di Gara (Admin), a seguito della validazione di tempi, ricorsi e classifiche.
          </p>
        </AccordionItem>

        <AccordionItem id="termini" icon={Ban} title="Termini di Servizio e Comportamento" open={open} toggle={toggle} testid="rules-termini">
          <ul className="space-y-2.5 text-sm text-gray-300 list-none">
            <li>• È consentito un solo account per partecipante.</li>
            <li>• Username, nomi di scuderie e leghe devono rispettare le linee guida: niente contenuti offensivi o inappropriati. L'organizzazione può rinominare o rimuovere contenuti non conformi.</li>
            <li>• Comportamenti scorretti (abuso di bug, account multipli, spam) comportano la sospensione dell'account.</li>
            <li>• I punteggi ufficiali sono quelli decretati dalla Direzione Gara; il gioco è a scopo ludico.</li>
            <li>• Le decisioni dell'amministrazione su punteggi e sanzioni sono definitive.</li>
          </ul>
        </AccordionItem>
      </div>
    </div>
  );
}
